// AGENCY-ADMIN ONLY surface. Used when an existing agent
// inside an agency requests an upgrade (e.g., adding new
// specializations or license info). Public agent self-signup
// was removed — see Register.tsx line 60. SuperAdmins and
// Sales staff should manage agencies, not individual agents.
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  CheckCircle2, XCircle, Clock, Eye, UserCheck, Building2, FileText, Download,
  Mail, Phone, MapPin, Globe, Calendar, User, Briefcase, Award, Hash, Image as ImageIcon,
  ArrowRight,
} from 'lucide-react';

const statusConfig = {
  pending: { label: 'Pending', icon: Clock, color: 'bg-yellow-100 text-yellow-800' },
  approved: { label: 'Approved', icon: CheckCircle2, color: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rejected', icon: XCircle, color: 'bg-red-100 text-red-800' },
};

const agentDocLabels: Record<string, string> = {
  photo_url: 'Applicant Photo',
  id_document_url: 'Identification Card',
};

const agencyDocLabels: Record<string, string> = {
  trade_license_url: 'Business License',
  trade_name_registration_url: 'Trade Registration',
  tin_certificate_url: 'TIN Certificate',
  vat_certificate_url: 'VAT Certificate',
  logo_url: 'Agency Logo',
};

export function AgentApplications() {
  const { user, hasRole } = useAuth();
  const queryClient = useQueryClient();
  const [selectedApp, setSelectedApp] = useState<any>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const isAgencyAdmin = hasRole('agency_admin') && !hasRole('superadmin');
  const isPlatformReviewer = hasRole('superadmin') ||
    hasRole('sales_manager') || hasRole('sales_team_leader') || hasRole('sales_agent');

  // For agency admins, first get their agency
  const { data: myAgency } = useQuery({
    queryKey: ['my-agency', user?.id],
    queryFn: async () => {
      const { data } = await supabase.from('agencies').select('id, name').eq('created_by', user!.id).single();
      return data;
    },
    enabled: !!user && isAgencyAdmin,
  });

  const { data: agentApps, isLoading: loadingAgents } = useQuery({
    queryKey: ['agent-applications', isAgencyAdmin ? myAgency?.id : 'all'],
    queryFn: async () => {
      let query = supabase.from('agent_applications' as any).select('*').order('created_at', { ascending: false });
      if (isAgencyAdmin && myAgency?.id) {
        query = query.eq('agency_id', myAgency.id);
      }
      const { data, error } = await query;
      if (error) throw error;
      return data as any[];
    },
    enabled: isAgencyAdmin ? !!myAgency : true,
  });

  // Fetch profiles for applicant info
  const allUserIds = (agentApps || []).map((a: any) => a.user_id);
  const { data: profiles } = useQuery({
    queryKey: ['agent-app-profiles', allUserIds],
    queryFn: async () => {
      if (!allUserIds.length) return {};
      const { data } = await supabase.from('profiles').select('*').in('user_id', allUserIds);
      return Object.fromEntries((data || []).map((p: any) => [p.user_id, p]));
    },
    enabled: allUserIds.length > 0,
  });

  const reviewMutation = useMutation({
    mutationFn: async ({ appId, status, notes }: { appId: string; status: string; notes: string }) => {
      const { error } = await supabase.from('agent_applications' as any).update({
        status, reviewer_notes: notes, reviewed_by: user!.id, reviewed_at: new Date().toISOString(),
      }).eq('id', appId);
      if (error) throw error;

      const app = agentApps?.find((a: any) => a.id === appId);
      if (!app) throw new Error('Application not found');

      if (status === 'approved') {
        const { error: profErr } = await supabase.from('agent_profiles').insert({
          user_id: app.user_id, license_number: app.license_number,
          experience_years: app.experience_years, specializations: app.specializations,
          areas_served: app.areas_served, is_verified: true,
          agency_id: app.agency_id || null,
        });
        if (profErr && !profErr.message.includes('duplicate')) throw profErr;
        const { error: roleErr } = await supabase.from('user_roles').insert({ user_id: app.user_id, role: 'agent' as any });
        if (roleErr && !roleErr.message.includes('duplicate')) throw roleErr;
      }

      // Notify applicant
      await supabase.from('notifications').insert({
        user_id: app.user_id,
        title: status === 'approved' ? 'Your agent application was approved' : 'Your agent application was rejected',
        message: notes || (status === 'approved' ? 'Welcome aboard!' : 'Please review and resubmit.'),
        type: status === 'approved' ? 'application_approved' : 'application_rejected',
        metadata: { application_id: app.id, application_type: 'agent' },
      });
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['agent-applications'] });
      toast.success(vars.status === 'approved'
        ? 'Agent application approved! Profile created and applicant notified.'
        : 'Application rejected. Applicant notified.');
      setSelectedApp(null);
      setReviewNotes('');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const getDocUrl = async (path: string) => {
    const { data } = await supabase.storage.from('developer-documents').createSignedUrl(path, 3600);
    if (data?.signedUrl) window.open(data.signedUrl, '_blank');
  };

  const viewDocImage = async (path: string) => {
    const { data } = await supabase.storage.from('developer-documents').createSignedUrl(path, 3600);
    if (data?.signedUrl) setPreviewImage(data.signedUrl);
  };

  const pendingAgents = agentApps?.filter((a: any) => a.status === 'pending').length || 0;

  const renderInfoRow = (icon: React.ReactNode, label: string, value: string | number | null | undefined) => {
    if (!value) return null;
    return (
      <div className="flex items-start gap-3 py-2">
        <div className="text-muted-foreground mt-0.5">{icon}</div>
        <div>
          <div className="text-xs text-muted-foreground font-medium">{label}</div>
          <div className="text-sm font-medium">{value}</div>
        </div>
      </div>
    );
  };

  const renderList = (apps: any[] | undefined, loading: boolean, type: 'agent' | 'agency', docLabels: Record<string, string>) => {
    if (loading) return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-16" />)}</div>;
    if (!apps?.length) return (
      <Card><CardContent className="py-12 text-center text-muted-foreground">
        {type === 'agent' ? <UserCheck className="h-12 w-12 mx-auto mb-3 opacity-50" /> : <Building2 className="h-12 w-12 mx-auto mb-3 opacity-50" />}
        <p>No {type} applications yet</p>
      </CardContent></Card>
    );
    return (
      <div className="space-y-3">
        {apps.map((app: any) => {
          const cfg = statusConfig[app.status as keyof typeof statusConfig] || statusConfig.pending;
          const profile = profiles?.[app.user_id];
          return (
            <Card key={app.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    {type === 'agent' ? <UserCheck className="h-6 w-6 text-primary" /> : <Building2 className="h-6 w-6 text-primary" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold">{type === 'agent' ? app.full_name : app.agency_name}</div>
                    <div className="text-sm text-muted-foreground">
                      {profile?.full_name ? `${profile.full_name} • ` : ''}{app.city || 'N/A'} • {new Date(app.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <Badge className={cfg.color}><cfg.icon className="h-3 w-3 mr-1" /> {cfg.label}</Badge>
                  <Button variant="outline" size="sm" onClick={() => { setSelectedApp({ ...app, _type: type, _docLabels: docLabels }); setReviewNotes(''); }}>
                    <Eye className="h-4 w-4 mr-1" /> Review
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  };

  const renderAgentDetails = (app: any) => (
    <div className="space-y-1">
      {renderInfoRow(<User className="h-4 w-4" />, 'Full Name', app.full_name)}
      {renderInfoRow(<Phone className="h-4 w-4" />, 'Phone', app.phone)}
      {renderInfoRow(<MapPin className="h-4 w-4" />, 'Address', app.address)}
      {renderInfoRow(<Briefcase className="h-4 w-4" />, 'Experience', app.experience_years ? `${app.experience_years} years` : null)}
      {renderInfoRow(<Phone className="h-4 w-4" />, 'WhatsApp', app.whatsapp)}
      {renderInfoRow(<Mail className="h-4 w-4" />, 'Telegram', app.telegram)}
    </div>
  );

  // Platform reviewers (SuperAdmin / Sales) should manage agencies, not individual agents.
  if (isPlatformReviewer && !isAgencyAdmin) {
    return (
      <Card className="rounded-2xl border-0 shadow-sm">
        <CardContent className="p-8 text-center space-y-4">
          <div className="h-14 w-14 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center">
            <Building2 className="h-7 w-7 text-primary" />
          </div>
          <div>
            <h3 className="font-display text-lg font-bold">Agents are managed by their agency</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
              Following the agency-owned model used by Zameen and Zillow, the platform reviews agencies — not individual agents.
              Each agency onboards and manages its own team members.
            </p>
          </div>
          <Button asChild>
            <Link to="/dashboard/agency-applications" className="gap-2">
              Go to Agency Applications <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-bold">Agent Applications</h2>
        {pendingAgents > 0 && <Badge className="bg-yellow-500 text-white">{pendingAgents} Pending</Badge>}
      </div>

      <div className="mt-4">{renderList(agentApps, loadingAgents, 'agent', agentDocLabels)}</div>

      {/* Review Dialog */}
      <Dialog open={!!selectedApp} onOpenChange={() => setSelectedApp(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-primary" />
              Agent Application: {selectedApp?.full_name}
            </DialogTitle>
            {selectedApp && (
              <div className="flex items-center gap-2 mt-1">
                <Badge className={statusConfig[selectedApp.status as keyof typeof statusConfig]?.color || ''}>
                  {statusConfig[selectedApp.status as keyof typeof statusConfig]?.label || selectedApp.status}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  Submitted {new Date(selectedApp.created_at).toLocaleDateString()} at {new Date(selectedApp.created_at).toLocaleTimeString()}
                </span>
              </div>
            )}
          </DialogHeader>


          {selectedApp && (
            <div className="space-y-5">
              {/* Section: Applicant Info */}
              <div>
                <h4 className="text-sm font-bold mb-3 flex items-center gap-2">
                  <User className="h-4 w-4 text-primary" />
                  Applicant Information
                </h4>
                <div className="border rounded-xl p-4 bg-muted/30">
                  {renderAgentDetails(selectedApp)}
                </div>
              </div>


              <Separator />

              {/* Section: Uploaded Documents */}
              <div>
                <h4 className="text-sm font-bold mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  Uploaded Documents
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {Object.entries(agentDocLabels).map(([field, label]) => {
                    const hasDoc = !!selectedApp[field];
                    const isImage = field === 'photo_url';
                    return (
                      <div key={field} className={`flex items-center justify-between p-3 rounded-lg border text-sm ${hasDoc ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' : 'bg-muted/50 border-dashed'}`}>
                        <div className="flex items-center gap-2">
                          {isImage ? <ImageIcon className={`h-4 w-4 ${hasDoc ? 'text-green-600' : 'text-muted-foreground'}`} /> :
                            <FileText className={`h-4 w-4 ${hasDoc ? 'text-green-600' : 'text-muted-foreground'}`} />}
                          <span className={hasDoc ? 'font-medium' : 'text-muted-foreground'}>{label}</span>
                          {hasDoc && <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />}
                          {!hasDoc && <span className="text-xs text-red-400">Not uploaded</span>}
                        </div>
                        {hasDoc && (
                          <div className="flex gap-1">
                            {isImage && (
                              <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => viewDocImage(selectedApp[field])}>
                                <Eye className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => getDocUrl(selectedApp[field])}>
                              <Download className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 text-xs text-muted-foreground">
                  {Object.keys(agentDocLabels).filter(f => !!selectedApp[f]).length} of {Object.keys(agentDocLabels).length} documents uploaded
                </div>

              </div>

              <Separator />

              {/* Previous review notes */}
              {selectedApp.reviewer_notes && (
                <div className="p-4 bg-muted rounded-xl">
                  <div className="text-xs font-bold mb-1">Previous Review Notes</div>
                  <p className="text-sm">{selectedApp.reviewer_notes}</p>
                  {selectedApp.reviewed_at && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Reviewed on {new Date(selectedApp.reviewed_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              )}

              {/* Action area */}
              {selectedApp.status === 'pending' && (
                <div className="space-y-3 pt-2">
                  <Textarea
                    placeholder="Add review notes — explain the reason if rejecting..."
                    value={reviewNotes}
                    onChange={e => setReviewNotes(e.target.value)}
                    rows={3}
                  />
                  <DialogFooter className="gap-2">
                    <Button variant="destructive" onClick={() => reviewMutation.mutate({ appId: selectedApp.id, status: 'rejected', notes: reviewNotes })} disabled={reviewMutation.isPending}>
                      <XCircle className="h-4 w-4 mr-1" /> Reject
                    </Button>
                    <Button onClick={() => reviewMutation.mutate({ appId: selectedApp.id, status: 'approved', notes: reviewNotes })} disabled={reviewMutation.isPending}>
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Approve & Create Profile
                    </Button>
                  </DialogFooter>

                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Document Preview</DialogTitle></DialogHeader>
          {previewImage && <img src={previewImage} alt="Document preview" className="w-full rounded-lg" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
